/**
 * AddDriverDialog Component
 *
 * Dialog for adding a new driver column to the board
 *
 * Features:
 * - Form validation
 * - Accessible dialog
 * - Submit/cancel actions
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface AddDriverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddDriver: (driverName: string) => void;
  isLoading?: boolean;
}

export function AddDriverDialog({
  open,
  onOpenChange,
  onAddDriver,
  isLoading = false,
}: AddDriverDialogProps) {
  const [driverName, setDriverName] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const trimmedName = driverName.trim();
    if (!trimmedName) {
      setError('Driver name is required');
      return;
    }
    if (trimmedName.length > 100) {
      setError('Driver name must be 100 characters or less');
      return;
    }

    // Submit
    onAddDriver(trimmedName);

    // Reset form
    setDriverName('');
    setError('');
    onOpenChange(false);
  };

  const handleCancel = () => {
    setDriverName('');
    setError('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Driver</DialogTitle>
            <DialogDescription>
              Add a new driver column to the dispatch board. Enter the driver&apos;s name below.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="space-y-2">
              <Label htmlFor="driverName">Driver Name</Label>
              <Input
                id="driverName"
                placeholder="e.g., John Smith"
                value={driverName}
                onChange={(e) => {
                  setDriverName(e.target.value);
                  setError('');
                }}
                disabled={isLoading}
                autoFocus
                aria-invalid={!!error}
                aria-describedby={error ? 'driverName-error' : undefined}
              />
              {error && (
                <p id="driverName-error" className="text-sm text-red-600">
                  {error}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Adding...' : 'Add Driver'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
